import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { httpsCallable } from 'firebase/functions';
import { useFunctions } from 'reactfire';
import {
  Box,
  Typography,
  Button,
  Paper,
  LinearProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';

export default function PdfScannerUpload({
  onPdfProcessed,
  onProcessingError,
  onStartProcessing,
  organizationId,
  certificateType: propCertificateType,
  onCertificateTypeChange,
}) {
  const functions = useFunctions();
  functions.region = 'europe-west1';

  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [certificateType, setCertificateType] = useState(propCertificateType || 'gauged_container');

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedFile(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const { result } = event.target || {};

      if (typeof result !== 'string') {
        reject(new Error('Invalid file data'));
        return;
      }

      // Strip the data URL prefix if present (e.g. "data:application/pdf;base64,")
      const commaIndex = result.indexOf(',');
      if (commaIndex === -1) {
        resolve(result);
        return;
      }

      const base64Payload = result.slice(commaIndex + 1);
      if (!base64Payload) {
        reject(new Error('Empty PDF payload'));
        return;
      }

      resolve(base64Payload);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(file);
  });

  const handleScanPdf = async () => {
    if (!uploadedFile) return;

    try {
      setUploading(true);
      setUploadProgress(0);
      onStartProcessing();

      // Convert file to base64
      setUploadProgress(20);
      const base64Data = await fileToBase64(uploadedFile);

      setUploadProgress(40);

      // Call Cloud Function to process PDF
      const processCertificatePdf = httpsCallable(
        functions,
        'processCertificatePdfCall2ndGen',
      );

      setUploadProgress(60);

      const result = await processCertificatePdf({
        pdfBase64: base64Data,
        fileName: uploadedFile.name,
        organizationId,
        certificateType,
      });

      setUploadProgress(100);

      if (result.data.success) {
        // Pass both extracted data, the original file, and scanId
        onPdfProcessed({
          ...result.data.data,
          pdfFile: uploadedFile, // Pass the original file for PDF viewer
          scanId: result.data.scanId, // Pass the scanId for navigation
        });
      } else {
        onProcessingError(result.data.error || 'Échec du traitement du PDF');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error processing PDF:', error);

      let errorMessage = 'Échec du traitement du PDF';

      if (error.code === 'functions/invalid-argument') {
        if (error.message.includes('too large')) {
          errorMessage = 'Fichier trop volumineux';
        } else if (error.message.includes('quota exceeded')) {
          errorMessage = 'Quota dépassé';
        } else if (error.message.includes('No height-volume tables')) {
          errorMessage = 'Aucun tableau hauteur-volume trouvé';
        } else if (error.message.includes('Low confidence')) {
          const score = Math.round(parseFloat(error.message.match(/\d+/)?.[0] || 0));
          errorMessage = `Confiance faible (${score}%)`;
        }
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = 'Fonctionnalité non activée';
      } else if (error.code === 'functions/not-found') {
        errorMessage = 'Format de fichier invalide';
      } else if (error.code === 'functions/unavailable') {
        errorMessage = 'Erreur réseau';
      } else if (error.code === 'functions/already-exists') {
        errorMessage = 'Ce document a déjà été scanné avec ce parser';
      }

      onProcessingError(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  return (
    <Box>
      {/* Certificate Type Selection */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Type de Certificat
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant={certificateType === 'gauged_container' ? 'contained' : 'outlined'}
            onClick={() => {
              setCertificateType('gauged_container');
              onCertificateTypeChange?.('gauged_container');
            }}
            disabled={uploading}
          >
            Contenant Jaugé
          </Button>
          <Tooltip title="Bientôt disponible">
            <span>
              <Button
                variant="outlined"
                disabled
              >
                Camion Citerne
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Upload Area */}
      <Paper
        onClick={getRootProps().onClick}
        onDragEnter={getRootProps().onDragEnter}
        onDragLeave={getRootProps().onDragLeave}
        onDragOver={getRootProps().onDragOver}
        onDrop={getRootProps().onDrop}
        sx={{
          p: 4,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <input
          accept={getInputProps().accept}
          multiple={getInputProps().multiple}
          onChange={getInputProps().onChange}
          ref={getInputProps().ref}
          style={getInputProps().style}
        />

        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />

        <Typography variant="h6" gutterBottom>
          {isDragActive
            ? 'Glisser-déposer le PDF ici'
            : 'Télécharger un PDF de certificat'}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Glisser-déposer ou cliquer pour sélectionner
          {' '}
          • PDF • Max 10MB
        </Typography>

        <Button variant="outlined" disabled={isDragActive}>
          Télécharger PDF
        </Button>
      </Paper>

      {/* Uploaded File Display */}
      {uploadedFile && (
        <Box sx={{ mt: 3 }}>
          <Paper sx={{
            p: 2, display: 'flex', alignItems: 'center', gap: 2,
          }}
          >
            <DescriptionIcon color="primary" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1">
                {uploadedFile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(uploadedFile.size / 1024 / 1024).toFixed(2)}
                {' '}
                MB
              </Typography>
            </Box>
            <Chip
              label="PDF"
              color="primary"
              variant="outlined"
              size="small"
            />
            <Button
              size="small"
              onClick={handleRemoveFile}
              disabled={uploading}
            >
              Supprimer
            </Button>
          </Paper>

          {/* Upload Progress */}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Traitement en cours...
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {/* Scan Button */}
          {!uploading && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleScanPdf}
                startIcon={<DescriptionIcon />}
              >
                Scanner le PDF
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
